// Pure view model of the transcript repair UI (PLAN-ADDENDUM A3, WP-8).
//
// A record-pass row's rawText is its engine words joined by single spaces, and
// every edit indexes those words ([startWord, endWord), end exclusive). The
// displayed text is the words with the APPLIED edits in place, exactly as the
// server renders it (repair/ledger.mjs renderRow). When a row no longer
// matches that render (a member's own text edit, a split), the UI shows its
// text plain and marks nothing: a span it cannot place is never guessed.
import type {
  CompanyMeeting,
  MeetingQualityReport,
  MeetingSegment,
  MeetingTranscriptEdit,
  MeetingTranscriptSpan,
} from './types';

export type UncertainSpan = MeetingTranscriptSpan & { reason: string };
export type RepairPiece =
  | { kind: 'text'; text: string }
  | { kind: 'edit'; text: string; edit: MeetingTranscriptEdit }
  | { kind: 'uncertain'; text: string; reason: string };

/** The engine words of a row: its raw text split at single spaces. */
export const rowWords = (segment: MeetingSegment): string[] =>
  segment.rawText ? segment.rawText.split(' ') : [];

const byStart = (
  left: { startWord: number; endWord: number },
  right: { startWord: number; endWord: number },
) => left.startWord - right.startWord || left.endWord - right.endWord;

/** The row's words with the applied edits in place (the server's render). */
export function renderApplied(words: string[], edits: MeetingTranscriptEdit[]): string {
  const out: string[] = [];
  let at = 0;
  for (const edit of [...edits].sort(byStart)) {
    if (edit.startWord < at) continue;
    for (; at < edit.startWord && at < words.length; at += 1) out.push(words[at]);
    if (edit.afterText) out.push(edit.afterText);
    at = Math.max(at, edit.endWord);
  }
  for (; at < words.length; at += 1) out.push(words[at]);
  return out.join(' ');
}

/** The quality report, only when it describes the transcript on screen. */
export function currentQuality(meeting: CompanyMeeting): MeetingQualityReport | null {
  const quality = meeting.quality;
  return quality && quality.transcriptRevision === meeting.transcriptRevision ? quality : null;
}

/** Open and applied edits of one row. */
export function rowEdits(meeting: CompanyMeeting, segmentIndex: number): MeetingTranscriptEdit[] {
  return (meeting.edits ?? [])
    .filter(
      (edit) =>
        edit.segmentIndex === segmentIndex &&
        edit.transcriptRevision === meeting.transcriptRevision &&
        (edit.status === 'applied' || edit.status === 'suggested'),
    )
    .sort(byStart);
}

/** Uncertain spans of one row that cover words (an insertion point marks nothing). */
export function rowUncertain(meeting: CompanyMeeting, segmentIndex: number): UncertainSpan[] {
  return (currentQuality(meeting)?.uncertainSpans ?? []).filter(
    (span) => span.segmentIndex === segmentIndex && span.endWord > span.startWord,
  );
}

type Marker =
  | { kind: 'edit'; startWord: number; endWord: number; edit: MeetingTranscriptEdit }
  | { kind: 'uncertain'; startWord: number; endWord: number; reason: string };

const overlaps = (left: Marker, right: Marker) =>
  left.startWord === right.startWord ||
  (left.startWord < right.endWord && right.startWord < left.endWord);

function markersOf(edits: MeetingTranscriptEdit[], uncertain: UncertainSpan[]): Marker[] {
  const markers: Marker[] = edits.map((edit) => ({
    kind: 'edit',
    startWord: edit.startWord,
    endWord: edit.endWord,
    edit,
  }));
  for (const span of uncertain) {
    const marker: Marker = {
      kind: 'uncertain',
      startWord: span.startWord,
      endWord: span.endWord,
      reason: span.reason,
    };
    if (!markers.some((kept) => overlaps(kept, marker))) markers.push(marker);
  }
  return markers.sort(byStart);
}

function markerPiece(marker: Marker, words: string[]): RepairPiece {
  const covered = words.slice(marker.startWord, marker.endWord).join(' ');
  if (marker.kind === 'uncertain')
    return { kind: 'uncertain', text: covered, reason: marker.reason };
  const text = marker.edit.status === 'applied' ? marker.edit.afterText : covered;
  return { kind: 'edit', text, edit: marker.edit };
}

/**
 * A row as pieces: plain text, edited spans (applied: the correction;
 * suggested: the words as heard) and uncertain spans. Null when the row's
 * displayed text is not the render of its applied edits.
 */
export function repairPieces(
  segment: MeetingSegment,
  edits: MeetingTranscriptEdit[],
  uncertain: UncertainSpan[],
): RepairPiece[] | null {
  const words = rowWords(segment);
  const applied = edits.filter((edit) => edit.status === 'applied');
  if (!words.length || renderApplied(words, applied) !== segment.displayText) return null;
  const pieces: RepairPiece[] = [];
  let at = 0;
  const flush = (until: number) => {
    if (until > at) pieces.push({ kind: 'text', text: words.slice(at, until).join(' ') });
    at = Math.max(at, until);
  };
  for (const marker of markersOf(edits, uncertain)) {
    if (marker.startWord < at) continue;
    flush(marker.startWord);
    pieces.push(markerPiece(marker, words));
    at = Math.max(at, marker.endWord);
  }
  flush(words.length);
  return pieces;
}

/** Applied and open suggestions of the current transcript. */
export function repairCounts(meeting: CompanyMeeting) {
  const edits = (meeting.edits ?? []).filter(
    (edit) => edit.transcriptRevision === meeting.transcriptRevision,
  );
  return {
    applied: edits.filter((edit) => edit.status === 'applied').length,
    suggested: edits.filter((edit) => edit.status === 'suggested').length,
  };
}

/** m:ss, or h:mm:ss from an hour. */
export function clock(ms: number | null | undefined): string {
  const total = Math.max(0, Math.floor((ms ?? 0) / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor(total / 60) % 60;
  const seconds = String(total % 60).padStart(2, '0');
  return hours
    ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}`
    : `${minutes}:${seconds}`;
}

/** Which actions an edit offers in its current status. */
export function editActions(edit: MeetingTranscriptEdit): ('accept' | 'reject' | 'undo')[] {
  if (edit.status === 'suggested') return ['accept', 'reject'];
  if (edit.status === 'applied') return ['undo'];
  if (edit.status === 'rejected' || edit.status === 'undone') return ['accept'];
  return [];
}
